import { readLocalStorageValue, useLocalStorage } from '@mantine/hooks';
import { QueryObserverResult, useQuery } from '@tanstack/react-query';
import {
  PayrollCycle,
  PayrollReportRowDto,
  PayrollReportView,
} from '@zorro/clients';
import { parseEnumValue } from '@zorro/common-utils';
import {
  formatDateISO,
  getFirstDayOfMonthEqualToOrBeforeDate,
} from '@zorro/shared/formatters';
import { recordsPerPage } from '@zorro/types';
import {
  PayrollFormData,
  PayrollViewToDataMap,
  mapPayrollReportRowDtoToRows,
} from '@zorro/web/shared-ui';
import { callEndpoint, useAppRouter } from '@zorro/web/ui-utils';
import { HttpStatusCode } from 'axios';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useMemo } from 'react';

async function getPage(
  payPeriodEndDate: Date,
  payrollCycle: PayrollCycle,
  indicateChangesSince: Date,
  page: number,
  employerId: string,
  payrollReportView: PayrollReportView
) {
  const referenceDate = formatDateISO(
    getFirstDayOfMonthEqualToOrBeforeDate(payPeriodEndDate)
  );

  return await callEndpoint({
    method: 'payrollReportControllerGeneratePayrollReport',
    params: [
      employerId,
      {
        referenceDate,
        payrollCycle,
        payPeriodEndDate: formatDateISO(payPeriodEndDate),
        indicateChangesSince: formatDateISO(indicateChangesSince),
        payrollReportView,
        perPage: recordsPerPage,
        page,
      },
    ],
  });
}

export function usePaginatedPayrollReports(
  employerId: string,
  payrollFormData?: PayrollFormData | null,
  view: PayrollReportView = PayrollReportView.ACTIONS_REQUIRED_SINCE
): {
  isLoading: boolean;
  data: PayrollReportRowDto[];
  refetch: () => Promise<QueryObserverResult<PayrollReportRowDto[] | null>>;
} {
  const { reloadPage } = useAppRouter();

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      'getGeneratePayrollReportArgs',
      [employerId, payrollFormData, view],
    ],
    queryFn: async () => {
      try {
        const payPeriodEndDate = payrollFormData?.payPeriodEndDate;
        const indicateChangesSince = payrollFormData?.indicateChangesSince;
        if (payrollFormData && payPeriodEndDate && indicateChangesSince) {
          const firstPage = await getPage(
            payPeriodEndDate,
            payrollFormData.payrollCycle,
            indicateChangesSince,
            1,
            employerId,
            view
          );
          if (firstPage) {
            const restOfPages = await Promise.all(
              Array.from({ length: firstPage.meta.lastPage - 1 }).map(
                (_, index) => {
                  const pageNumber = index + 2;
                  return getPage(
                    payPeriodEndDate,
                    payrollFormData.payrollCycle,
                    indicateChangesSince,
                    pageNumber,
                    employerId,
                    view
                  );
                }
              )
            );
            return [firstPage, ...restOfPages].flatMap((page) => page.data);
          }
        }
      } catch (error) {
        if (error.status === HttpStatusCode.Unauthorized) {
          reloadPage();
        }
      }
      return null;
    },
  });
  return {
    isLoading,
    data: data ?? [],
    refetch,
  };
}

export function useFetchPayrollReportAllViews(
  employerId: string,
  payrollFormData?: PayrollFormData | null
) {
  const storedView = readLocalStorageValue<string>({
    key: `selectedView_${employerId}`,
    defaultValue: PayrollReportView.ACTIONS_REQUIRED_SINCE,
  });

  const [viewQuery, setViewQuery] = useQueryState(
    'view',
    parseAsString.withDefault('')
  );

  const [viewLocal, setViewLocalLocal] = useLocalStorage({
    key: `selectedView_${employerId}`,
    defaultValue: storedView ?? PayrollReportView.ACTIONS_REQUIRED_SINCE,
  });

  useEffect(() => {
    if (viewQuery && viewQuery !== viewLocal) {
      setViewLocalLocal(viewQuery);
    } else if (
      !viewQuery &&
      viewLocal !== PayrollReportView.ACTIONS_REQUIRED_SINCE
    ) {
      void setViewQuery(viewLocal);
    }
  }, []);

  const setCurrentView = (view: PayrollReportView) => {
    void setViewQuery(view);
    setViewLocalLocal(view);
  };

  const {
    isLoading: isLoadingActionsRequiredSince,
    data: actionsRequiredSinceData = [],
  } = usePaginatedPayrollReports(
    employerId,
    payrollFormData,
    PayrollReportView.ACTIONS_REQUIRED_SINCE
  );

  const { isLoading: isLoadingFullPayPeriod, data: fullPayPeriodData = [] } =
    usePaginatedPayrollReports(
      employerId,
      payrollFormData,
      PayrollReportView.FULL_PAY_PERIOD
    );

  const payrollViewToDataMap: PayrollViewToDataMap = useMemo(() => {
    if (isLoadingActionsRequiredSince || isLoadingFullPayPeriod) {
      return new Map([
        [
          PayrollReportView.ACTIONS_REQUIRED_SINCE,
          { isLoading: isLoadingActionsRequiredSince, data: undefined },
        ],
        [
          PayrollReportView.FULL_PAY_PERIOD,
          { isLoading: isLoadingFullPayPeriod, data: undefined },
        ],
      ]);
    }

    return new Map([
      [
        PayrollReportView.ACTIONS_REQUIRED_SINCE,
        {
          isLoading: isLoadingActionsRequiredSince,
          data: mapPayrollReportRowDtoToRows(actionsRequiredSinceData),
        },
      ],
      [
        PayrollReportView.FULL_PAY_PERIOD,
        {
          isLoading: isLoadingFullPayPeriod,
          data: mapPayrollReportRowDtoToRows(fullPayPeriodData),
        },
      ],
    ]);
  }, [
    isLoadingActionsRequiredSince,
    actionsRequiredSinceData,
    isLoadingFullPayPeriod,
    fullPayPeriodData,
  ]);

  return {
    payrollViewToDataMap,
    currentView:
      parseEnumValue(PayrollReportView, viewLocal) ||
      PayrollReportView.ACTIONS_REQUIRED_SINCE,
    setCurrentView,
  };
}
